import React from 'react';

// HOOKS
import {useSelector} from 'react-redux';

// COMPONENTS
import ArrowRight from '../../../../../stories/Icons/ArrowRight';
import {PrimaryLink} from '../../../../../stories/components/CommonStyleUtils/dwebCommon.styles';
import BreadcrumbsDetailsLoader from '../../../../../stories/components/hotels/Loadingpage/BreadcrumbDetails/BreadcrumbDetailsLoader';

import * as S from './Breadcrumb.styles';

// COLOR PALETTE AND THEME
import {defaultTheme} from '@stencil/build-theme';

// TYPES
import {LinkProps} from 'react-router-dom';

// REDUX RESELECT SELECTORS
import {
  isStaticDataLoading as isStaticDataLoadingSelectorFn,
  breadcrumbSelector,
} from '@stencil/hotels-details-reducer/src/selectors';

type BreadCrumbProps = {};

type BreadCrumbItemType = {
  children: React.ReactNode;
  to: LinkProps['to'];
  positionForStructuredSEO: string;
};

type BreadCrumbItemAnchorType = {
  children: React.ReactText;
  href: string;
  positionForStructuredSEO: string;
};

export const listItemSEOProps = {
  itemScope: true,
  itemProp: 'itemListElement',
  itemType: 'http://schema.org/ListItem',
};

const BreadCrumbItem: React.FC<BreadCrumbItemType> = ({children, to, positionForStructuredSEO}) => (
  <li {...listItemSEOProps}>
    <S.BreadCrumbItemLink to={to} itemProp="item">
      <span itemProp="name">{children}</span>
    </S.BreadCrumbItemLink>
    <meta itemProp="position" content={positionForStructuredSEO} />
  </li>
);

const BreadCrumbItemAnchor: React.FC<BreadCrumbItemAnchorType> = ({
  children,
  href,
  positionForStructuredSEO,
}) => (
  <li {...listItemSEOProps}>
    <PrimaryLink href={href} itemProp="item">
      <span itemProp="name">{children}</span>
    </PrimaryLink>
    <meta itemProp="position" content={positionForStructuredSEO} />
  </li>
);

type NativeAnchorBreadCrumbItemType = {
  children: React.ReactNode;
  href: string;
  positionForStructuredSEO: string;
};

const NativeAnchorBreadCrumbItem: React.FC<NativeAnchorBreadCrumbItemType> = ({
  children,
  href,
  positionForStructuredSEO,
}) => (
  <li {...listItemSEOProps}>
    <S.BreadCrumbATagItemLink href={href} itemProp="item">
      <span itemProp="name">{children}</span>
    </S.BreadCrumbATagItemLink>
    <meta itemProp="position" content={positionForStructuredSEO} />
  </li>
);

type NoLinkBreadcrumbItemTypes = {
  children: React.ReactNode;
  positionForStructuredSEO: string;
};

const NoLinkBreadcrumbItem: React.FC<NoLinkBreadcrumbItemTypes> = ({
  children,
  positionForStructuredSEO,
}) => (
  <S.NoLinkBreadcrumbListItem {...listItemSEOProps}>
    <span itemProp="name">{children}</span>
    <meta itemProp="position" content={positionForStructuredSEO} />
  </S.NoLinkBreadcrumbListItem>
);

const RightArrow = () => (
  <S.ArrowWrapper>
    <ArrowRight width={'1rem'} fill={defaultTheme.colors.downriver.dark} />
  </S.ArrowWrapper>
);

const Breadcrumb: React.FC<BreadCrumbProps> = () => {
  const {
    cityName,
    cityId,
    hotelCityURL = '',
    hotelName = '',
    checkin,
    checkout,
    roomString,
    countryUrl,
    countryName,
  } = useSelector(breadcrumbSelector);

  const isStaticDataLoading = useSelector(isStaticDataLoadingSelectorFn);

  const srpHotelQueryParam = {
    ci: checkin,
    co: checkout,
    r: roomString,
  };

  // const SRPLinkBack = `/hotels/find-hotels-in-${cityName}/${cityId}/${cityId}/${JSON.stringify(
  //   srpHotelQueryParam,
  // )}`;

  const srpCityURL = hotelCityURL;

  if (isStaticDataLoading) {
    return <BreadcrumbsDetailsLoader />;
  }

  const showCountry = !!countryUrl;
  const showCity = !!cityName && srpCityURL;

  let positionForStructuredSEOForHotelName = '3';

  if (showCountry && showCity) {
    positionForStructuredSEOForHotelName = '5';
  }

  if ((!showCountry && showCity) || (showCountry && !showCity)) {
    positionForStructuredSEOForHotelName = '4';
  }

  return (
    <S.OuterWrapperDiv>
      <S.BreadCrumbContainer itemScope itemType="https://schema.org/breadcrumb">
        <S.BreadCrumbList itemScope itemType="http://schema.org/BreadcrumbList">
          <BreadCrumbItemAnchor href="/" positionForStructuredSEO="1">
            Goibibo
          </BreadCrumbItemAnchor>
          <BreadCrumbItem to="/hotels/" positionForStructuredSEO="2">
            <>
              <RightArrow />
              <span>Hotel Booking</span>
            </>
          </BreadCrumbItem>

          {showCountry && (
            <NativeAnchorBreadCrumbItem positionForStructuredSEO="3" href={countryUrl}>
              <>
                <RightArrow />
                <span>{countryName}</span>
              </>
            </NativeAnchorBreadCrumbItem>
          )}

          {showCity && (
            <NativeAnchorBreadCrumbItem
              positionForStructuredSEO={countryUrl ? '4' : '3'}
              href={srpCityURL}
            >
              <RightArrow />
              <span>{cityName}</span>
            </NativeAnchorBreadCrumbItem>
          )}

          {!!hotelName && (
            <NoLinkBreadcrumbItem positionForStructuredSEO={positionForStructuredSEOForHotelName}>
              <RightArrow />
              <span>{hotelName}</span>
            </NoLinkBreadcrumbItem>
          )}
        </S.BreadCrumbList>
      </S.BreadCrumbContainer>
    </S.OuterWrapperDiv>
  );
};

export default Breadcrumb;
